/**
 * Admin Team Management — May 4, 2026.
 *
 * Admin → Settings → Team Management. Lets admins:
 *   • Create teams
 *   • Rename / change leader / deactivate teams
 *   • Manage members (add / remove / reassign across teams)
 *
 * Stripe / Notion-style. Pure React + Tailwind, uses existing
 * shadcn/ui primitives. Backend endpoints:
 *   GET    /api/teams
 *   POST   /api/teams
 *   PUT    /api/teams/{id}
 *   GET    /api/teams/{id}/members
 *   POST   /api/teams/{id}/members/{user_id}
 *   DELETE /api/teams/{id}/members/{user_id}
 *   GET    /api/users
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Users, X, Search, AlertTriangle, Check, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

// Roles eligible to lead a team — backend allows any user as leader_id;
// this set is the dropdown allowlist that mirrors the approval hierarchy:
//   • Tax / Title teams       → *_search_team_leader
//   • IT / HR teams           → it_manager / hr_manager (their leave
//                               requests auto-escalate to Admin per
//                               hr_module.py self-approval block)
//   • Any team                → admin (catch-all)
// Jun 2026 — extended to include it_manager + hr_manager after Ops
// reported Sharath Kumar (IT Manager) couldn't be selected as IT Team
// leader. See `frontend/src/utils/roleNormalizer.js` for canonical
// label→slug mapping.
const LEADER_ROLES = new Set([
  'admin',
  'team_leader',
  'tax_search_team_leader',
  'title_search_team_leader',
  'it_manager',
  'hr_manager',
]);

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Avatar = ({ name }) => {
  const initials = (name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
      {initials}
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return formatDateOnly(iso);
  } catch {
    return '—';
  }
};

export default function AdminTeamManagement() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberCounts, setMemberCounts] = useState({}); // {team_id: count}
  const [editingTeam, setEditingTeam] = useState(null); // null | {} (create) | team
  const [memberDrawerTeam, setMemberDrawerTeam] = useState(null);
  // Pagination — presentation only (list is already fully loaded by the
  // existing architecture, capped at 100 teams server-side)
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const totalPages = Math.max(1, Math.ceil(teams.length / perPage));
  const pagedTeams = useMemo(
    () => teams.slice((page - 1) * perPage, page * perPage),
    [teams, page, perPage]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [teamsRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/teams`, auth()),
        axios.get(`${API}/api/users`, auth()),
      ]);
      const teamsList = teamsRes.data?.teams || teamsRes.data || [];
      const usersList = usersRes.data?.users || usersRes.data || [];

      // Member counts + userId→teamId map (single pass, /api/users lacks team_id)
      const counts = {};
      const userTeamMap = {};
      await Promise.all(teamsList.map(async (t) => {
        try {
          const r = await axios.get(`${API}/api/teams/${t.id}/members`, auth());
          const ms = r.data?.members || [];
          counts[t.id] = r.data?.count ?? ms.length;
          ms.forEach((m) => { if (m?.id) userTeamMap[m.id] = t.id; });
        } catch {
          counts[t.id] = 0;
        }
      }));

      const enrichedUsers = usersList
        .filter((u) => u.is_active !== false)
        .map((u) => ({ ...u, team_id: userTeamMap[u.id] || null }));

      setTeams(teamsList);
      setUsers(enrichedUsers);
      setMemberCounts(counts);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-8" data-testid="admin-team-management">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-blue-600 shadow-sm flex items-center justify-center flex-shrink-0">
            <Users className="h-[22px] w-[22px] text-white" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 leading-tight">Team Management</h1>
            <p className="text-[12px] text-slate-500">Manage teams, leaders, members and Active Users viewers</p>
          </div>
        </div>
        <Button
          onClick={() => setEditingTeam({})}
          className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
          data-testid="create-team-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Create Team
        </Button>
      </div>

      {/* Teams Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-sm text-slate-400">Loading teams…</div>
        ) : teams.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">No teams yet — click <strong>Create Team</strong> to get started.</p>
          </div>
        ) : (
          <table className="w-full" data-testid="teams-table">
            <thead style={{ background: 'linear-gradient(135deg, #003c8f 0%, #004aad 55%, #0d55b8 100%)' }}>
              <tr className="text-[10.5px] font-semibold text-white/95 uppercase tracking-wider">
                <th className="text-left px-5 py-3" style={{ background: 'transparent', color: 'rgba(255,255,255,0.95)' }}>Team</th>
                <th className="text-left px-4 py-3" style={{ background: 'transparent', color: 'rgba(255,255,255,0.95)' }}>Leader</th>
                <th className="text-left px-4 py-3" style={{ background: 'transparent', color: 'rgba(255,255,255,0.95)' }}>Members</th>
                <th className="text-left px-4 py-3" style={{ background: 'transparent', color: 'rgba(255,255,255,0.95)' }}>Status</th>
                <th className="text-left px-4 py-3" style={{ background: 'transparent', color: 'rgba(255,255,255,0.95)' }}>Created</th>
                <th className="text-right px-5 py-3" style={{ background: 'transparent', color: 'rgba(255,255,255,0.95)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedTeams.map((t) => {
                const leader = users.find(u => u.id === t.leader_id);
                return (
                  <tr key={t.id} className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40 transition-colors" data-testid={`team-row-${t.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="font-semibold text-sm text-slate-900">{t.name}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{t.code}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      {leader ? (
                        <div className="flex items-center gap-2">
                          <Avatar name={leader.full_name} />
                          <div>
                            <div className="text-sm font-medium text-slate-900">{leader.full_name}</div>
                            <div className="text-[11px] text-slate-500 capitalize">{(leader.role || '').replace(/_/g, ' ')}</div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">— no leader —</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                        <Users className="w-3.5 h-3.5 text-blue-500" />
                        {memberCounts[t.id] ?? 0} {memberCounts[t.id] === 1 ? 'Member' : 'Members'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      {t.is_active === false ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Inactive
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        {formatDate(t.created_at)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => setEditingTeam(t)}
                        title="Edit team"
                        aria-label="Edit team"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors mr-1"
                        data-testid={`edit-team-${t.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setMemberDrawerTeam(t)}
                        title="Manage members"
                        aria-label="Manage members"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        data-testid={`manage-members-${t.id}`}
                      >
                        <Users className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Standard TitleFlow pagination */}
        {!loading && teams.length > 0 && (
          <div className="px-5 py-2.5 border-t border-slate-200 bg-slate-50/60 flex flex-wrap items-center gap-3" data-testid="teams-pagination">
            <span className="text-[12px] text-slate-500" data-testid="teams-pagination-summary">
              Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, teams.length)} of {teams.length} teams
            </span>
            <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
              <span>Rows per page</span>
              <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                className="h-7 border border-slate-200 rounded-md px-1.5 bg-white text-slate-600 focus:outline-none"
                data-testid="teams-per-page-select">
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            {totalPages > 1 && (
              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="h-7 px-2 rounded-md border border-slate-200 bg-white text-[12px] font-medium text-slate-600 disabled:opacity-40"
                  data-testid="teams-pagination-prev">‹ Previous</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`h-7 min-w-[28px] px-1.5 rounded-md text-[12px] font-semibold transition-colors ${p === page ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                    data-testid={`teams-pagination-page-${p}`}>{p}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="h-7 px-2 rounded-md border border-slate-200 bg-white text-[12px] font-medium text-slate-600 disabled:opacity-40"
                  data-testid="teams-pagination-next">Next ›</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {editingTeam !== null && (
        <TeamFormModal
          team={editingTeam}
          users={users}
          memberCount={memberCounts[editingTeam.id] || 0}
          onClose={() => setEditingTeam(null)}
          onSaved={() => { setEditingTeam(null); fetchAll(); }}
        />
      )}

      {/* Manage Members Drawer */}
      {memberDrawerTeam && (
        <ManageMembersDrawer
          team={memberDrawerTeam}
          allUsers={users}
          allTeams={teams}
          onClose={() => setMemberDrawerTeam(null)}
          onChanged={fetchAll}
        />
      )}
    </div>
  );
}

/* =========================================================
   TeamFormModal — create OR edit
   ========================================================= */
const TeamFormModal = ({ team, users, memberCount, onClose, onSaved }) => {
  const isCreate = !team?.id;
  const [name, setName] = useState(team?.name || '');
  const [code, setCode] = useState(team?.code || '');
  const [leaderId, setLeaderId] = useState(team?.leader_id || '');
  const [active, setActive] = useState(team?.is_active !== false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // TEAM-SCOPE-005: view-only Active Users viewers (Team Leader roles only)
  const [viewerIds, setViewerIds] = useState(team?.active_users_viewer_ids || []);
  const [viewerSearch, setViewerSearch] = useState('');

  const eligibleLeaders = useMemo(
    () => users.filter(u => LEADER_ROLES.has(u.role) && (
      !search.trim() ||
      (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase())
    )),
    [users, search]
  );

  const leaderChanged = !isCreate && leaderId && leaderId !== team.leader_id;
  const VIEWER_TL_ROLES = ['team_leader', 'tax_search_team_leader', 'title_search_team_leader'];
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const viewerCandidates = useMemo(() => {
    const q = viewerSearch.trim().toLowerCase();
    return users.filter(u =>
      VIEWER_TL_ROLES.includes(u.role) &&
      u.id !== leaderId &&
      !viewerIds.includes(u.id) &&
      (!q || (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
    );
  }, [users, leaderId, viewerIds, viewerSearch]);
  const viewersChanged = !isCreate && JSON.stringify([...viewerIds].sort()) !== JSON.stringify([...(team.active_users_viewer_ids || [])].sort());
  const deactivating = !isCreate && team.is_active !== false && active === false;
  const reactivating = !isCreate && team.is_active === false && active === true;

  const submit = async () => {
    if (!name.trim() || !leaderId) {
      toast.error('Team name and leader are required.');
      return;
    }
    if (isCreate && !code.trim()) {
      toast.error('Team code is required for new teams.');
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        await axios.post(`${API}/api/teams`, {
          name: name.trim(), code: code.trim(), leader_id: leaderId,
        }, auth());
        toast.success('Team created');
      } else {
        const payload = {};
        if (name.trim() !== team.name) payload.name = name.trim();
        if (leaderId !== team.leader_id) payload.leader_id = leaderId;
        if (active !== (team.is_active !== false)) payload.is_active = active;
        if (viewersChanged) payload.active_users_viewer_ids = viewerIds;
        if (Object.keys(payload).length === 0) {
          toast.info('No changes to save.');
          setSaving(false);
          return;
        }
        await axios.put(`${API}/api/teams/${team.id}`, payload, auth());
        toast.success('Team updated');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save team';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px] p-6" data-testid="team-form-modal">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'Create Team' : 'Edit Team'}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {isCreate ? 'Configure the team name, code, and leader.' : 'Update team details. Changing the leader re-routes leave approvals.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="team-name" className="text-xs font-medium">Team Name</Label>
            <Input
              id="team-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Title Search Team A"
              className="mt-1"
              data-testid="team-name-input"
            />
          </div>

          {isCreate && (
            <div>
              <Label htmlFor="team-code" className="text-xs font-medium">Team Code</Label>
              <Input
                id="team-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g., TST-A"
                className="mt-1 font-mono"
                data-testid="team-code-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Short unique code, used in audit logs and reports.</p>
            </div>
          )}

          <div>
            <Label className="text-xs font-medium">Team Leader</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search admins, team leaders, IT/HR managers…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7 h-9"
              />
            </div>
            <div className="mt-2 max-h-[180px] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
              {eligibleLeaders.length === 0 ? (
                <div className="text-xs text-slate-400 px-3 py-4 text-center">No matching admins, team leaders, or IT/HR managers.</div>
              ) : eligibleLeaders.map(u => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setLeaderId(u.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors ${leaderId === u.id ? 'bg-indigo-50 border-l-2 border-l-indigo-600' : ''}`}
                  data-testid={`leader-option-${u.id}`}
                >
                  <Avatar name={u.full_name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{u.full_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{(u.role || '').replace(/_/g, ' ')} · {u.email}</div>
                  </div>
                  {leaderId === u.id && <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {leaderChanged && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                Changing team leader will update leave approval routing for {memberCount} member{memberCount === 1 ? '' : 's'}.
              </p>
            </div>
          )}

          {!isCreate && (
            <div className="pt-2 border-t border-slate-100">
              <Label className="text-xs font-medium">Additional Active Users Viewers</Label>
              <p className="text-[11px] text-slate-500 mt-0.5 mb-1.5">
                View-only access to Activity → Active Users for this team. No leave approval, ownership, or any other Team Leader authority.
              </p>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="team-viewers-chips">
                {viewerIds.length === 0 && (
                  <span className="text-[11px] text-slate-400 italic">No additional viewers</span>
                )}
                {viewerIds.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full pl-2.5 pr-1 py-0.5" data-testid={`viewer-chip-${id}`}>
                    {userById[id]?.full_name || id}
                    <button type="button" onClick={() => setViewerIds(v => v.filter(x => x !== id))}
                      className="h-4 w-4 rounded-full hover:bg-blue-200 flex items-center justify-center text-blue-600"
                      data-testid={`viewer-remove-${id}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-slate-400" />
                <Input
                  placeholder="Search Team Leaders to add as viewers…"
                  value={viewerSearch}
                  onChange={(e) => setViewerSearch(e.target.value)}
                  className="pl-7 h-9"
                  data-testid="viewer-search-input"
                />
              </div>
              {viewerSearch.trim() && (
                <div className="mt-1.5 max-h-[140px] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {viewerCandidates.length === 0 ? (
                    <div className="text-xs text-slate-400 px-3 py-3 text-center">No matching Team Leaders.</div>
                  ) : viewerCandidates.map(u => (
                    <button key={u.id} type="button"
                      onClick={() => { setViewerIds(v => [...v, u.id]); setViewerSearch(''); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`viewer-option-${u.id}`}>
                      <Avatar name={u.full_name} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{u.full_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{(u.role || '').replace(/_/g, ' ')} · {u.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isCreate && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div>
                <Label className="text-xs font-medium">Status</Label>
                <p className="text-[11px] text-slate-500 mt-0.5">{deactivating ? 'Will deactivate' : reactivating ? 'Will reactivate' : team.is_active === false ? 'Inactive' : 'Active'}</p>
              </div>
              <button
                type="button"
                onClick={() => setActive(!active)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${active ? 'bg-emerald-500' : 'bg-slate-300'}`}
                data-testid="team-active-toggle"
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${active ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>
          )}

          {deactivating && memberCount > 0 && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-2.5">
              <AlertTriangle className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-800">
                This team has {memberCount} active member{memberCount === 1 ? '' : 's'}. Please reassign them before deactivation.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || (deactivating && memberCount > 0)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="team-save-btn"
          >
            {saving ? 'Saving…' : isCreate ? 'Create Team' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* =========================================================
   ManageMembersDrawer
   ========================================================= */
const ManageMembersDrawer = ({ team, allUsers, allTeams, onClose, onChanged }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reassignConfirm, setReassignConfirm] = useState(null); // {user, fromTeam}
  const [acting, setActing] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/teams/${team.id}/members`, auth());
      setMembers(r.data?.members || []);
    } catch (err) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [team.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const memberIds = useMemo(() => new Set(members.map(m => m.id)), [members]);
  const teamById = useMemo(() => Object.fromEntries(allTeams.map(t => [t.id, t])), [allTeams]);

  const candidates = useMemo(() => {
    const q = search.toLowerCase();
    return allUsers.filter(u =>
      !memberIds.has(u.id) &&
      u.id !== team.leader_id &&
      (
        !q ||
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      )
    );
  }, [allUsers, memberIds, search, team.leader_id]);

  const doAdd = async (user) => {
    setActing(true);
    try {
      await axios.post(`${API}/api/teams/${team.id}/members/${user.id}`, {}, auth());
      toast.success(`${user.full_name} added to ${team.name}`);
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setActing(false);
      setReassignConfirm(null);
    }
  };

  const handleAdd = (user) => {
    if (user.team_id && user.team_id !== team.id) {
      setReassignConfirm({ user, fromTeam: teamById[user.team_id] });
      return;
    }
    doAdd(user);
  };

  const doRemove = async (user) => {
    setActing(true);
    try {
      await axios.delete(`${API}/api/teams/${team.id}/members/${user.id}`, auth());
      toast.success(`${user.full_name} removed`);
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setActing(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/40 z-50"
        onClick={onClose}
        data-testid="member-drawer-backdrop"
      />
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white shadow-2xl z-[51] flex flex-col" data-testid="member-drawer">
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{team.name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{members.length} member{members.length === 1 ? '' : 's'}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 -m-1" aria-label="Close" data-testid="member-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Current Members */}
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Current Members</h4>
            {loading ? (
              <p className="text-xs text-slate-400 text-center py-4">Loading…</p>
            ) : members.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No members yet — add some from the list below.</p>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                {members.map(m => (
                  <li key={m.id} className="flex items-center gap-2 px-3 py-2" data-testid={`member-row-${m.id}`}>
                    <Avatar name={m.full_name} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{m.full_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{(m.role || '').replace(/_/g, ' ')}</div>
                    </div>
                    <button
                      onClick={() => doRemove(m)}
                      disabled={acting}
                      className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      data-testid={`remove-member-${m.id}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Add Members */}
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Add Members</h4>
            <div className="relative mb-2">
              <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search users…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7 h-9"
                data-testid="member-search-input"
              />
            </div>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-[280px] overflow-y-auto">
              {candidates.length === 0 ? (
                <li className="text-xs text-slate-400 text-center py-4">No matching users.</li>
              ) : candidates.slice(0, 100).map(u => (
                <li key={u.id} className="flex items-center gap-2 px-3 py-2" data-testid={`candidate-row-${u.id}`}>
                  <Avatar name={u.full_name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{u.full_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {(u.role || '').replace(/_/g, ' ')}
                      {u.team_id && u.team_id !== team.id && (
                        <span className="ml-1 text-amber-700">· {teamById[u.team_id]?.name || 'another team'}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleAdd(u)}
                    disabled={acting}
                    className="text-xs text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded hover:bg-indigo-50 transition-colors font-medium"
                    data-testid={`add-member-${u.id}`}
                  >
                    Add
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      {/* Reassign confirm */}
      {reassignConfirm && (
        <Dialog open onOpenChange={(o) => !o && setReassignConfirm(null)}>
          <DialogContent className="sm:max-w-[400px]" data-testid="reassign-confirm">
            <DialogHeader>
              <DialogTitle>Move user?</DialogTitle>
              <DialogDescription className="text-xs text-slate-500 pt-2">
                Move <strong>{reassignConfirm.user.full_name}</strong> from{' '}
                <strong>{reassignConfirm.fromTeam?.name || 'their current team'}</strong> to{' '}
                <strong>{team.name}</strong>?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setReassignConfirm(null)}>Cancel</Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={() => doAdd(reassignConfirm.user)}
                data-testid="reassign-confirm-btn"
              >
                Confirm Move
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
